<template>
	<div class="content-page content-page--schedule-dashboard">
		<div class="content-page__heading" ref="contentDashBoardHeading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">Schedule Dashboard</h2>
			</div>
		</div>

		<BlockNotifications
			title="Selected clients"
			message="A maximum of 20 clients will be allowed to be added to a schedule at any given time."
			:blockState="'error'"
			:blockVariant="'small'"
			ref="blockNotification"
			v-if="showBlockNotification"
		/>

		<div class="schedule-dashboard">
			<div class="schedule-dashboard__head">
				<span class="schedule-dashboard__head-label">{{
					setTitleDashboard
				}}</span>

				<ButtonGroup>
					<template #container>
						<Button
							:className="'btn--primary'"
							:labelText="applyButtonLabel"
							:btnClick="saveAction"
							:disabled="disableButton"
						/>
						<Button
							labelText="Cancel"
							:btnClick="redirectToDashboard"
						/>
					</template>
				</ButtonGroup>
			</div>

			<div class="schedule-dashboard__form-list" v-if="hasResults">
				<ScheduleAddEditForm
					@hasError="updateHasErrorModal"
					@hasDateError="updateHasDateError"
					v-model="schedule"
				/>

				<SelectList
					:title="'Clients'"
					placeholder="Search by client name or number"
					:itemAvailables="availableCustomers"
					:itemSelected="selectedCustomers"
					:mapping="mappingFields"
					@change="setSelectedCustomers"
				>
				</SelectList>
			</div>

			<Spinner :processor="{ loading: loading }" />
		</div>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import ButtonGroup from '@/components/forms/button/button-group';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import ScheduleAddEditForm from '@/sections/environment/schedule/shared/environment-schedule-add-edit-form.vue';
import SelectList from '@/components/forms/selection-list/selection-list';
import Spinner from '@/components/visual/spinner/spinner';
import BlockNotifications from '@/components/notifications/block-notifications/block-notifications';

import { mapActions } from 'vuex';
import { CURRENT_SCHEDULE } from '@/sections/environment/environment-mutation-type.js';

//utils
import { formatDate } from '@/utils/datetime';
import { deepEqual as objCompare } from '@/utils/object';
import { compareArraysOfObjects } from '@/utils/function';

//vuex
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';
import CustomerOptionsMixin from '@/mixins/customers/customer-options';
import { CUSTOMER_OPTIONS } from '@/sections/options/options-mutation-type';
import _ from 'lodash';

export default {
	name: 'EnvironmentScheduleCreateEditDashBoard',
	mixins: [CustomerOptionsMixin],
	components: {
		Button,
		ButtonGroup,
		Breadcrumbs,
		ScheduleAddEditForm,
		SelectList,
		Spinner,
		BlockNotifications
	},
	props: {
		id: {
			type: Number
		}
	},
	data() {
		return {
			schedule: { customers: [] },
			originalSchedule: { customers: [] },
			selectedCustomers: [],
			hasError: true,
			hasDateError: false,
			loading: false,
			hasSameCustomers: true,
			disableButton: true,
			defaultUrlNames: [
				{
					name: 'Zone Manager',
					path: '/environments'
				},
				{
					name: 'Dashboard',
					path: '/environments/schedule'
				},
				{
					name: 'Schedule'
				}
			]
		};
	},

	async created() {
		if (this.id) {
			try {
				this.loading = true;

				await this.getScheduleById(this.id);
			} finally {
				this.loading = false;
			}
		}
	},
	computed: {
		...mapGetters('customers', ['customerById']),
		...mapState('environments', [CURRENT_SCHEDULE]),

		actionPage() {
			if (this.id) {
				if (this.$route.path.includes('edit')) {
					return 'edit';
				}
				return 'reschedule';
			}

			return 'create';
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);
			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}
			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.defaultUrlNames;
		},
		customers() {
			const { items } = this[CUSTOMER_OPTIONS];
			return items || [];
		},
		availableCustomers() {
			if (!this.customers.length) return [];
			return this.customers.map(item => {
				return {
					text: item.text,
					value: item.value
				};
			});
		},
		mappingFields() {
			return {
				id: 'value',
				name: 'text'
			};
		},
		setTitleDashboard() {
			let action = this.actionPage;
			return `${action.charAt(0).toUpperCase()}${action.slice(
				1
			)} a scheduled move`;
		},
		applyButtonLabel() {
			const actionLabel = {
				create: 'Create a Scheduled Move',
				edit: 'Save Changes',
				reschedule: 'Reschedule'
			};
			return actionLabel[this.actionPage];
		},
		hasScheduleChanges() {
			const areEqualsObj = objCompare(
				{ ...this.schedule },
				{ ...this.originalSchedule },
				true,
				[
					'rowCreatedById',
					'customers',
					'removedCustomers',
					'rowModifiedById',
					'rowModifiedDate',
					'statusCode'
				]
			);

			return !areEqualsObj;
		},
		hasResults() {
			return !this.loading && !_.isEmpty(this.availableCustomers);
		},
		showBlockNotification() {
			const { selectedCustomers } = this;
			return selectedCustomers.length > 20;
		}
	},

	methods: {
		...mapActions('environments', {
			getSecheduleById: 'getSecheduleById',
			saveScheduleAction: 'saveSchedule'
		}),
		...mapActions('customers', ['loadCustomers']),

		async getScheduleById(id) {
			await this.getSecheduleById(id).then(resp => {
				this.parseResponse(resp);
			});
		},
		parseResponse(obj) {
			let schedule = _.omit(obj, ['removedCustomers', 'customers']);
			if (obj.customers.length > 0) {
				const mapCustomers = propName => {
					return obj[propName].map(item => {
						return {
							text: item.customerName,
							value: item.customerId
						};
					});
				};
				const customers = mapCustomers('customers');
				const removedCustomers = mapCustomers('removedCustomers');
				Object.assign(schedule, {
					customers: customers,
					removedCustomers: removedCustomers
				});
			}
			if (schedule.releaseType !== 'R' && schedule.releaseType !== 'H') {
				schedule.releaseType = null;
			}

			schedule.scheduledDate = formatDate(schedule.scheduledDate);

			Object.assign(this.schedule, schedule);
			this.selectedCustomers = _.cloneDeep(this.schedule.customers);
			this.originalSchedule = _.cloneDeep(this.schedule);
		},

		updateHasErrorModal(val) {
			this.hasError = val;
			this.disableButton = this.disabledSaveActionButton();
		},
		setSelectedCustomers(customers) {
			Vue.set(this, 'selectedCustomers', customers);
			Vue.set(this.schedule, 'customers', customers);
		},
		async saveAction() {
			const mapCustomers = propName => {
				return this.schedule[propName].map(item => {
					return {
						customerId: item.value
					};
				});
			};

			let payload = _.omit(this.schedule, [
				'removedCustomers',
				'customers'
			]);
			const customers = mapCustomers('customers');

			Object.assign(payload, {
				customers: customers
			});
			if (this.actionPage === 'reschedule') {
				payload.rescheduleId = payload.id;
				payload.reschedulingNeeded = true;
				delete payload.id;
			}

			await this.saveScheduleAction(payload).then(isSuccessful => {
				if (isSuccessful) {
					this.redirectToDashboard();
				}
			});
		},

		redirectToDashboard() {
			this.$router.push({
				path: `/environments/schedule`
			});
		},
		compareCustomers() {
			this.hasSameCustomers = compareArraysOfObjects(
				[...this.selectedCustomers],
				[...this.originalSchedule.customers]
			);
		},
		disabledSaveActionButton() {
			const {
				actionPage,
				hasError,
				hasDateError,
				selectedCustomers,
				hasScheduleChanges,
				hasSameCustomers,
				schedule: { scheduledDate, moveToZoneId, releaseType, comment }
			} = this;

			if (hasDateError) {
				return true;
			}

			if (selectedCustomers.length > 20) {
				return true;
			}

			if (actionPage === 'create') {
				return hasError || selectedCustomers.length === 0;
			}

			if (actionPage === 'edit') {
				if (hasScheduleChanges && selectedCustomers.length !== 0) {
					return hasError;
				}
				if (hasSameCustomers || selectedCustomers.length === 0) {
					return true;
				}
			}

			if (actionPage === 'reschedule') {
				const hasEmptyFields =
					!scheduledDate || !moveToZoneId || !releaseType || !comment;
				if (selectedCustomers.length === 0 || hasEmptyFields) {
					return true;
				}
			}

			return false;
		},
		updateHasDateError(val) {
			this.hasDateError = val;
			this.disableButton = this.disabledSaveActionButton();
		}
	},
	watch: {
		selectedCustomers: {
			deep: true,
			immediate: true,
			handler() {
				this.compareCustomers();
			}
		},
		schedule: {
			deep: true,
			immediate: true,
			handler() {
				this.disableButton = this.disabledSaveActionButton();
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Schedule Dashboard Page
	* Can use SCSS variables = TRUE
**/
.schedule-dashboard {
	&__head {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	&__head-label {
		@include heading('md');
	}

	&__form-list {
		display: grid;
		grid-template-columns: 1fr;
		gap: $gutter-xl;
		margin-top: $gutter-lg;

		@include mq($from: md-l) {
			grid-template-columns: 1fr 2fr;
			gap: $gutter-xxl;
		}
	}
}

// SelectionList control tweaks for Schedule Dashboard UI

::v-deep .selection-list {
	column-gap: $gutter-xxl;

	@include mq($until: md-l) {
		gap: $gutter-xl;
	}
}

::v-deep .selection-list__available,
::v-deep .selection-list__selected {
	padding: 0;
}
::v-deep .selection-list__items {
	max-height: calc(100vh - 420px);

	@include mq($until: md-l) {
		max-height: calc(100vh - 550px);
	}
}

::v-deep .selection-list__selected .selection-list__items {
	max-height: calc(100vh - 376px);

	@include mq($until: md-l) {
		max-height: calc(100vh - 506px);
	}
}

// Shared add/edit form tablet

@include mq($until: md-l) {
	::v-deep .schedule-add-edit-form {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: $gutter-lg;
	}
	::v-deep .schedule-add-edit-form__group {
		gap: $gutter-lg;
	}
}
</style>
